"use client";

import { useMemo, useState } from "react";
import { resolveText } from "./binding";
import type { MacaronRenderContext, RecordAny } from "./types";

type TabItem = { title: string; child: string };

function resolveTabItems(config: RecordAny, ctx: MacaronRenderContext): TabItem[] {
  if (!Array.isArray(config.tabItems)) return [];
  return config.tabItems
    .filter((item): item is RecordAny => Boolean(item && typeof item === "object"))
    .slice(0, 3)
    .map((item, index) => {
      const child = typeof item.child === "string" ? item.child : "";
      const title = resolveText(item.title, ctx.data) || `Tab ${index + 1}`;
      return child ? { title, child } : null;
    })
    .filter((item): item is TabItem => Boolean(item));
}

export function MacaronTabs({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const tabItems = useMemo(() => resolveTabItems(config, ctx), [config, ctx]);
  const [active, setActive] = useState(0);
  const safeActive = Math.max(0, Math.min(active, Math.max(0, tabItems.length - 1)));
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  if (tabItems.length === 0) return null;

  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      <div className="grid w-full rounded-full bg-[#E9E7E2] p-[3px]" style={{ gridTemplateColumns: `repeat(${tabItems.length}, minmax(0, 1fr))` }}>
        {tabItems.map((item, index) => (
          <button
            key={`${item.child}-${index}`}
            type="button"
            className={`min-h-9 rounded-full px-3 py-1 text-center text-[13px] font-medium text-[#161615] transition-colors ${index === safeActive ? "bg-white" : "bg-transparent"}`}
            onClick={() => setActive(index)}
          >
            {item.title}
          </button>
        ))}
      </div>
      <div
        className="min-w-0"
        onTouchStart={(event) => setTouchStartX(event.touches[0]?.clientX ?? null)}
        onTouchEnd={(event) => {
          if (touchStartX == null) return;
          const endX = event.changedTouches[0]?.clientX ?? touchStartX;
          const delta = endX - touchStartX;
          if (Math.abs(delta) >= 24) {
            if (delta < 0 && safeActive < tabItems.length - 1) setActive(safeActive + 1);
            if (delta > 0 && safeActive > 0) setActive(safeActive - 1);
          }
          setTouchStartX(null);
        }}
      >
        {tabItems[safeActive] ? ctx.renderChild(tabItems[safeActive]!.child) : null}
      </div>
    </div>
  );
}
